



































































































































































import { Component, Vue } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import MainModule from '../store/modules/MainModule';
import Icon from './generic/Icon.vue';
import ViewSelector from './gateways/views/ViewSelector.vue';
import Spinner from './generic/Spinner.vue';
import TasksDisplay from './displays/TasksDisplay.vue';
import MilestonesDisplay from './displays/MilestonesDisplay.vue';
import NoData from './gateways/views/NoData.vue';
import {
  User,
  Source,
  AuthenticatableSource,
  SourceVisitor,
  PaginatedListOfTasks,
  PaginatedListOfMilestones,
  Project,
} from 'ganttlab-entities';
import { ImplementedSourcesGateways } from '../helpers/ImplementedSourcesGateways';
import { DisplayableError } from '../helpers/DisplayableError';
import { addDisplaybleError } from '../helpers';
import { TasksAndMilestones } from 'ganttlab-use-cases';
import LocalForage, { getRememberedViews } from '../helpers/LocalForage';
import { trackVirtualpageView, trackInteractionEvent } from '../helpers/GTM';

const mainState = getModule(MainModule);

@Component({
  components: {
    Icon,
    ViewSelector,
    Spinner,
    TasksDisplay,
    MilestonesDisplay,
    NoData,
  },
})
export default class Home extends Vue {
  public paginatedTasks: PaginatedListOfTasks | null = null;
  public paginatedMilestones: PaginatedListOfMilestones | null = null;

  setTasksPage(page: number) {
    mainState.setViewGatewayTasksPage(page);
    this.refresh(false);
    trackInteractionEvent(
      'View',
      'Changed tasks page',
      this.viewGateway ? this.viewGateway.slug : undefined,
    );
  }

  setMilestonesPage(page: number) {
    mainState.setViewGatewayMilestonesPage(page);
    this.refresh(false);

    trackInteractionEvent(
      'View',
      'Changed milestones page',
      this.viewGateway ? this.viewGateway.slug : undefined,
    );
  }

  setActiveMilestone(index: number) {
    mainState.setViewGatewayActiveMilestone(index);
    // reset task page to avoid weird behaviors, that one will also refresh
    this.setTasksPage(1);
    trackInteractionEvent(
      'View',
      'Changed active milestone',
      this.viewGateway ? this.viewGateway.slug : undefined,
    );
  }

  get user(): User | null {
    return mainState.user;
  }

  // source gateway is stored in vuex store
  get sourceGateway(): Source | AuthenticatableSource | null {
    if (mainState.sourceGateway) {
      return mainState.sourceGateway;
    }
    return null;
  }

  get sourceUrl(): string | null {
    if (
      mainState.sourceGateway &&
      mainState.sourceGateway instanceof AuthenticatableSource
    ) {
      return mainState.sourceGateway.getUrl();
    }
    return null;
  }

  get sourceLogoIcon(): string | null {
    if (mainState.sourceGateway) {
      const gatewayDetails = ImplementedSourcesGateways.find((gateway) => {
        if (mainState.sourceGateway) {
          return gateway.slug === mainState.sourceGateway.slug;
        }
      });
      if (gatewayDetails) {
        return gatewayDetails.icon;
      }
    }
    return null;
  }

  // view gateway is stored in vuex store
  get viewGateway(): SourceVisitor<unknown> | null {
    if (mainState.viewGateway) {
      return mainState.viewGateway;
    }
    return null;
  }

  // there might be a project in the view gateway configuration
  get project(): Project | null {
    if (this.viewGateway && this.viewGateway.configuration.project) {
      return this.viewGateway.configuration.project;
    }
    return null;
  }

  // there might be an active milestone in the view gateway configuration
  get activeMilestone(): number | null {
    if (
      this.viewGateway &&
      this.viewGateway.configuration.activeMilestone !== undefined
    ) {
      return this.viewGateway.configuration.activeMilestone;
    }
    return null;
  }

  async setView(view: SourceVisitor<unknown>) {
    if (!this.sourceGateway) {
      return;
    }
    // clear data so we get the spinner back while loading the new ones
    this.paginatedTasks = null;
    this.paginatedMilestones = null;
    // get the view data
    try {
      const data = await this.sourceGateway.getDataFor(view);
      if (data instanceof PaginatedListOfTasks) {
        this.paginatedTasks = data;
      }
      if (data instanceof PaginatedListOfMilestones) {
        this.paginatedMilestones = data;
      }
      if (data instanceof TasksAndMilestones) {
        this.paginatedTasks = data.tasks;
        this.paginatedMilestones = data.milestones;
      }
      trackVirtualpageView(
        `${this.sourceGateway.name} - ${view.name}`,
        `/${this.sourceGateway.slug}/${view.slug}`,
      );
    } catch (error) {
      addDisplaybleError(
        new DisplayableError(error, 'Unable to get view data'),
      );
      trackInteractionEvent('View', 'Error', view.slug);
      this.paginatedTasks = new PaginatedListOfTasks([], 1, 0);
    }

    // user okay to remember? let's do it!
    const remember = await LocalForage.getItem('remember');
    let viewsBySource = await getRememberedViews();
    if (remember) {
      if (!viewsBySource) viewsBySource = {};
      viewsBySource[this.sourceGateway.slug] = {
        slug: view.slug,
        configuration: view.configuration,
      };
      LocalForage.setItem('viewsBySource', viewsBySource);
    }
  }

  async refresh(manual = true) {
    if (this.viewGateway) {
      this.setView(this.viewGateway);
      if (manual) {
        trackInteractionEvent('View', 'Refreshed', this.viewGateway.slug);
      }
    }
  }

  logout() {
    mainState.logout();
    trackInteractionEvent(
      'Authentication',
      'Logged out',
      this.sourceGateway ? this.sourceGateway.slug : undefined,
    );
  }

  goToWebsite() {
    trackInteractionEvent('Click', 'Website');
  }

  goToSource() {
    trackInteractionEvent(
      'Click',
      'Source',
      this.sourceGateway ? this.sourceGateway.slug : undefined,
    );
  }

  goToProject() {
    trackInteractionEvent('Click', 'Project');
  }

  goToUser() {
    trackInteractionEvent('Click', 'User');
  }

  goToHelp() {
    const source = this.sourceGateway
      ? `Source ${this.sourceGateway.slug}`
      : undefined;
    const view = this.viewGateway ? `View ${this.viewGateway.slug}` : undefined;
    const agg = [source, view];
    trackInteractionEvent('Click', 'Help', agg.join(' - '));
  }
}
